import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';

import WrapperWidget from '../Wrapper';
import Icon from '../../../Common/Icon';
import InputText from '../../../FormFields/Text';
import InputNumber from '../../../FormFields/Number';
import RadioWrapper from '../../../FormFields/WrapperRadio';
import InputSelect from '../../../FormFields/Select';
import Checkbox from '../../../FormFields/Checkbox';
import withConditionsAndTriggers from '../withConditionsAndTriggers';
import { SCORE_OPTIONS, TAGS_AVAILABLE, IS_ADMIN } from '../../../../Context/DynamicFormContext';
import ConfigService from '../../../../Services/api/Config';
import TagSelect from '../../../Common/NewSelect';
import { ReactComponent as DragIcon } from '../../../../../img/dform_dragndrop_icon.svg';
import DraggableList from '../../../Common/DraggableList';


export const Rating3ptWidget = (props) => {
  const { field, renderForm, value, error, onChange, onError, api } = props;
  const onChangeOptions = useCallback((value, changeFieldValue, changeFieldError, options, index, attr) => {
    options[index][attr] = value;
    changeFieldValue('options', options);
    const error = checkLabelsValidation(options);
    changeFieldError('options', error);
  }, []);

  const onDragSelectItem = useCallback((dragEvent, changeFieldValue, options) => {
    const { source, destination } = dragEvent;
    if (!source || !destination) return;
    
    const item = options.splice(source.index, 1)[0];
    options.splice(destination.index, 0, item);
    changeFieldValue('options', options);
  }, []);

  const checkLabelsValidation = useCallback((options) => {
    // Ensure exactly 3 options
    if (options.length !== 3) {
      return 'This component must have exactly 3 options.';
    }

    let result = '';
    options.forEach((option, index) => {
      // Check if any option is empty
      if (!option.value) {
        result = 'All options should have some label.';
      } else if (option.value.length > 6) {
        result = 'Options must be 6 characters or less.';
      } else {
        // Check for duplicates
        options.forEach((optionCompared, indexCompared) => {
          if (option.value.toLowerCase() === optionCompared.value.toLowerCase() && indexCompared !== index) {
            result = 'This field can not be repeated.';
          }
        });
      }
    });
    return result;
  }, []);

  // eslint-disable-next-line max-params
  const renderConfigOption = useCallback(
    (option, index, internalField, changeFieldValue, changeFieldError) => {
      const { options, errors } = internalField;
      let tagTypeOptions = [];

      const getOptionValue = () => {
        if (
          internalField.updated_in_language &&
          internalField[internalField.updated_in_language] &&
          internalField[internalField.updated_in_language].options[index]
        ) {
          return internalField[internalField.updated_in_language].options[index].label;
        } else if (
          internalField.created_in_language &&
          internalField[internalField.created_in_language.code] &&
          internalField[internalField.created_in_language.code].options[index]
        ) {
          return internalField[internalField.created_in_language.code].options[index].label;
        }
        return option.value;
      };

      if (TAGS_AVAILABLE && TAGS_AVAILABLE.length > 0) {
        TAGS_AVAILABLE.forEach((item) => {
          if (item.allow_use_in_dynamic_forms) {
            tagTypeOptions.push({ value: item.id, label: item.display_name || item.tag_name, tag_name: item.tag_name });
          }
          if (!item.allow_use_in_dynamic_forms) {
            if (option.tags_available && option.tags_available.length > 0) {
              option.tags_available.filter((data, index) => {
                if (data == item.id) {
                  option.tags_available.splice(index, 1);
                }
              });
            }
          }
        });
      }

      return (
        <div className="Table" key={index}>
          <div className="TableCell">
            <div className={'Draggable__Holder'}>
              <Icon width="15" height="15" color="tertiaryColor" position="Left">
                <DragIcon alt="value_dragndrop_icon" />
              </Icon>
            </div>
          </div>
          <div className="TableCell">
            <InputText
              value={getOptionValue()}
              onChange={(v) => onChangeOptions(v, changeFieldValue, changeFieldError, options, index, 'value')}
              onError={(error) => changeFieldError('options', error)}
              error={errors['options']}
              notShowErrors={true}
              validators={[() => checkLabelsValidation(options)]}
            />
          </div>
          <div className="TableCell">
            <InputNumber value={option.score} onChange={(v) => onChangeOptions(v, changeFieldValue, changeFieldError, options, index, 'score')} />
          </div>
          <div className="TableCell Center">
            <Checkbox
              onChange={(v) => onChangeOptions(v, changeFieldValue, changeFieldError, options, index, 'lockDDR')}
              label=""
              checked={option.lockDDR}
            />
          </div>
          <div className="TableCell">
            <TagSelect
              isMulti={true}
              onValueChange={(v) => onChangeOptions(v, changeFieldValue, changeFieldError, options, index, 'tags_available')}
              value={option.tags_available}
              controlShouldRenderValue={false}
              showValuesContainer={false}
              availableTags={tagTypeOptions}
              isAdmin={IS_ADMIN}
            />
          </div>
          <div className="TableCell"></div>
        </div>
      );
    },
    [onChangeOptions, checkLabelsValidation]
  );

  const renderConfigTab = useCallback(
    (internalField, changeFieldValue, changeFieldError) => {
      // Ensure there are exactly 3 options
      if (internalField.options.length !== 3) {
        if (internalField.options.length < 3) {
          // Add missing options up to 3
          while (internalField.options.length < 3) {
            const defaultValues = ['Bad', 'OK', 'Great'];
            const nextIndex = internalField.options.length;
            internalField.options.push({
              value: defaultValues[nextIndex] || `Option ${nextIndex + 1}`,
              score: ''
            });
          }
        } else {
          // Trim options to exactly 3
          internalField.options = internalField.options.slice(0, 3);
        }
        changeFieldValue('options', internalField.options);
      }

      // Force default value to be the middle option ("OK")
      const middleOptionValue = internalField.options[1].value;
      if (internalField.defaultValue !== middleOptionValue) {
        changeFieldValue('defaultValue', middleOptionValue);
      }

      return (
        <div className="Widget_Form_Config">
          <InputText
            label="Label"
            required
            value={api.getFieldsVerbiage(internalField, 'label')}
            onChange={(v) => changeFieldValue('label', v)}
            error={internalField.errors['label']}
            onError={(error) => changeFieldError('label', error)}
          />
          <InputText
            label="Description"
            value={api.getFieldsVerbiage(internalField, 'description')}
            onChange={(v) => changeFieldValue('description', v)}
          />
          <InputSelect label="Score type" value={internalField.scoreType} onChange={(v) => changeFieldValue('scoreType', v)} options={SCORE_OPTIONS} />
         

          <div className="Widget_Form_Config_Options">
            <div className="Table">
              <div className="TableCell"></div>
              <div className="TableCell">
                <div className="InputWrapper-Label">Value</div>
              </div>
              <div className="TableCell">
                <div className="InputWrapper-Label">Score</div>
              </div>
              <div className="TableCell">
                <div className="InputWrapper-Label Center">Lock DDR</div>
              </div>
              <div className="TableCell">
                <div className="InputWrapper-Label Center">Tag</div>
              </div>
              <div className="TableCell"></div>
            </div>

            <DraggableList
              list={internalField.options}
              renderItem={(option, index) => renderConfigOption(option, index, internalField, changeFieldValue, changeFieldError)}
              onDragFinish={(props) => onDragSelectItem(props, changeFieldValue, internalField.options)}
            />
            {internalField.errors.options && (
              <div className="InputWrapper InputWrapper_Error">
                <div className="InputWrapper-Error" style={{ color: ConfigService.getColor('errorColor') }}>
                  {internalField.errors.options}
                </div>
              </div>
            )}
          </div>

          <div className="form-group form-item mt-3">
            <p className="help-block">Default answer is always the middle option ({internalField.options[1].value}).</p>
          </div>

          <div className="InputCheckboxesWrapper">
            <InputSelect
              label="Display Width"
              value={internalField.display_width}
              onChange={(v) => changeFieldValue('display_width', v.value)}
              options={
                field.displayWidthOptions &&
                field.displayWidthOptions.map((option) => {
                  return { value: option.value, label: option.value };
                })
              }
            />
          </div>

          <div className="InputCheckboxesWrapper">
            <Checkbox
              onChange={(v) => changeFieldValue('labelInline', v)}
              label="Label inline"
              checked={internalField.labelInline}
              className="InputCheckbox_Block"
            />
            <Checkbox
              onChange={(v) => changeFieldValue('required', v)}
              label="Required"
              checked={internalField.required}
              className="InputCheckbox_Block"
            />
            <Checkbox onChange={(v) => changeFieldValue('photo', v)} label="Photo" checked={internalField.photo} className="InputCheckbox_Block" />
            <Checkbox onChange={(v) => changeFieldValue('notes', v)} label="Notes" checked={internalField.notes} className="InputCheckbox_Block" />
          </div>

          <div className="InputCheckboxesWrapper">
            <Checkbox
              onChange={(v) => changeFieldValue('lockDDR', v)}
              label="Lock in DDR"
              checked={internalField.lockDDR}
              className="InputCheckbox_Block"
            />
            <Checkbox
              onChange={(v) => changeFieldValue('vertical', v)}
              label="Justified Left"
              checked={internalField.vertical}
              className="InputCheckbox_Block"
            />
            <Checkbox
              onChange={(v) => changeFieldValue('compact', v)}
              label="Compact"
              checked={internalField.compact}
              className="InputCheckbox_Block"
            />
            <Checkbox
              onChange={(v) => changeFieldValue('force_line_break', v)}
              label="Start new line / row"
              checked={internalField.force_line_break}
              className="InputCheckbox_Block"
            />
          </div>
        </div>
      );
    },
    [api, field, renderConfigOption]
  );

  const widgetProps = { ...props, renderForm: () => renderForm(renderConfigTab) };

  return (
    <WrapperWidget {...widgetProps}>
      <div className="Rating3ptWidget">
        <RadioWrapper
          required={field.required}
          disabled={field.disabled}
          compact={field.compact}
          value={value}
          onChange={onChange}
          onError={onError}
          options={
            api
              ? api.getFieldsVerbiage(field, 'options')
              : field.options.map((item, index) => {
                return { value: index, label: item.value };
              })
          }
          name={field.id}
          className={classNames({
            InputRadio_Block: field.vertical,
            InputRadio_Compact: field.compact
          })}
          error={error}
        />
      </div>
    </WrapperWidget>
  );
};

export const Rating3ptWidgetEditable = withConditionsAndTriggers(Rating3ptWidget);
